/**
 * 홈 통계 Mock Store
 * 데이터 자산 오버뷰 및 증가 추이를 제공합니다.
 */
import dayjs from 'dayjs';

import type { ChartData, ChartDataPoint } from '@/shared/api/openmetadata';

/**
 * 날짜별 자산 증가 추이 생성
 * - table: 실제 iceberg 테이블 24개 기준 (MAGENTA)
 * - run: 실험 실행 데이터 (PURPLE)
 * - feature_set: ML Feature Set 데이터 (CHARTREUSE)
 * - dataset: 데이터셋 (YELLOW)
 */
export const getDataAssetsTrendMockData = (period: number): ChartData => {
  const results: ChartDataPoint[] = [];
  const today = dayjs().startOf('day');

  // 현재 총 자산 수 (표시 순서: Table > Run > Feature Set > Dataset)
  const currentTotals = {
    table: 24, // ALL_ICEBERG_DATA 기준 24개
    run: 18, // 두 번째
    feature_set: 12, // 세 번째
    dataset: 8, // 네 번째
  };

  // 기간별 시작 자산 수 (현재에서 역산)
  const startRatios = {
    7: 0.85, // 7일 전에는 현재의 85%
    14: 0.75, // 14일 전에는 현재의 75%
    30: 0.6, // 30일 전에는 현재의 60%
    90: 0.4, // 90일 전에는 현재의 40%
  };

  const startRatio = startRatios[period as keyof typeof startRatios] || 0.85;

  // 각 타입별 시작 수량
  const startTotals = {
    table: Math.floor(currentTotals.table * startRatio),
    run: Math.floor(currentTotals.run * startRatio),
    feature_set: Math.floor(currentTotals.feature_set * startRatio),
    dataset: Math.floor(currentTotals.dataset * startRatio),
  };

  // 날짜별 증가량 계산
  const increments = {
    table: (currentTotals.table - startTotals.table) / period,
    run: (currentTotals.run - startTotals.run) / period,
    feature_set: (currentTotals.feature_set - startTotals.feature_set) / period,
    dataset: (currentTotals.dataset - startTotals.dataset) / period,
  };

  // 날짜별 데이터 생성 (과거부터 현재까지)
  for (let i = period - 1; i >= 0; i--) {
    const currentDate = today.subtract(i, 'day');
    const dayTimestamp = currentDate.valueOf();
    const daysSinceStart = period - i - 1;

    // 각 타입별 누적 수량 계산 (소수점 아래 반올림하되 자연스러운 증가)
    const tableCount = Math.round(startTotals.table + increments.table * daysSinceStart);
    const runCount = Math.round(startTotals.run + increments.run * daysSinceStart);
    const featureSetCount = Math.round(startTotals.feature_set + increments.feature_set * daysSinceStart);
    const datasetCount = Math.round(startTotals.dataset + increments.dataset * daysSinceStart);

    // 각 자산 타입별로 데이터 포인트 추가
    results.push({
      count: tableCount,
      day: dayTimestamp,
      group: 'table',
    });

    results.push({
      count: runCount,
      day: dayTimestamp,
      group: 'run',
    });

    results.push({
      count: featureSetCount,
      day: dayTimestamp,
      group: 'feature_set',
    });

    results.push({
      count: datasetCount,
      day: dayTimestamp,
      group: 'dataset',
    });
  }

  return { results };
};

/**
 * 특정 차트 이름에 대한 Mock 데이터 반환
 */
export const getChartMockData = (chartName: string, startDate: number, endDate: number): ChartData => {
  // 기간 계산 (일 단위)
  const period = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

  switch (chartName) {
    case 'total_data_assets':
      return getDataAssetsTrendMockData(period);
    default:
      return { results: [] };
  }
};
